/**
 * C1 — Banner de instalação PWA.
 *
 * Comportamento por plataforma:
 *   - Android (Chrome/Edge): ouve `beforeinstallprompt`, guarda o evento,
 *     mostra banner "Instalar Nexo"; click → prompt.prompt().
 *   - iOS Safari (não-standalone): banner com "Como instalar" abre modal
 *     explicando o caminho manual (Compartilhar → Adicionar à Tela
 *     Inicial). Safari NÃO suporta `beforeinstallprompt`.
 *   - Desktop, ou já-instalado (display-mode standalone): nada renderiza.
 *
 * Persistência: dismissal fica em localStorage com timestamp. Se o user
 * dispensou nos últimos 7 dias, não mostra de novo.
 *
 * Aparece com delay de 30s OU na 2ª pageview (o que vier primeiro), pra
 * não atrapalhar o primeiro contato com o app.
 */
import { useCallback, useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";

const STORAGE_KEY = "nexo-install-dismissed";
const PAGEVIEW_KEY = "nexo-install-pageviews";
const DELAY_MS = 30_000;
const REDISPLAY_AFTER_DAYS = 7;

function _isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad em iOS 13+ se reporta como Mac — daí o teste de touch.
  return /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Mac") && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1);
}

function _isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari expõe a flag legada `navigator.standalone`.
    window.navigator?.standalone === true
  );
}

function _wasDismissedRecently() {
  try {
    const ts = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    if (!ts) return false;
    const ageDays = (Date.now() - ts) / 86_400_000;
    return ageDays < REDISPLAY_AFTER_DAYS;
  } catch { return false; }
}

function _bumpPageviews() {
  try {
    const n = parseInt(localStorage.getItem(PAGEVIEW_KEY) || "0", 10) + 1;
    localStorage.setItem(PAGEVIEW_KEY, String(n));
    return n;
  } catch { return 1; }
}


export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [iosModal, setIosModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState(null); // "android" | "ios" | null

  useEffect(() => {
    // Filtros iniciais: standalone / dismissed / desktop sem prompt
    if (_isStandalone()) return;
    if (_wasDismissedRecently()) return;

    const pageviews = _bumpPageviews();
    const ios = _isIOS();

    // Android: vamos esperar o evento beforeinstallprompt.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setPlatform("android");
      // Decide quando exibir: 2ª pageview imediato, senão delay.
      if (pageviews >= 2) setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS: o evento nunca vai disparar — programamos exibição direto.
    let timer = null;
    if (ios) {
      setPlatform("ios");
      if (pageviews >= 2) {
        setShow(true);
      } else {
        timer = setTimeout(() => setShow(true), DELAY_MS);
      }
    } else {
      // Android: se o evento já tiver vindo (raro mas possível antes do
      // listener montar), o prompt entra; senão esperamos. Damos um delay
      // de fallback pra cobrir desktops sem prompt nativo.
      timer = setTimeout(() => {
        if (deferredPrompt) setShow(true);
      }, DELAY_MS);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* */ }
    setShow(false);
    setIosModal(false);
  }, []);

  const installAndroid = useCallback(async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      // Se aceitou OU rejeitou, escondemos o banner. Em rejeição, o
      // dismissal de 7d evita encher o saco.
      setDeferredPrompt(null);
      setShow(false);
      if (choice?.outcome === "dismissed") {
        try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* */ }
      }
    } catch {
      setShow(false);
    }
  }, [deferredPrompt]);

  if (!show) return null;

  return (
    <>
      {/* Banner rodapé. FaseA T3: respeita home indicator do iOS (env() = 0
          em desktop — no-op). */}
      <div
        data-testid="pwa-install-prompt"
        className="fixed bottom-[calc(env(safe-area-inset-bottom,0px)+16px)] left-4 right-4 z-[60] mx-auto max-w-md p-4 rounded-xl bg-surface-card border border-brand-cyan/30 shadow-lg backdrop-blur-md flex items-center gap-3"
        style={{ background: "rgba(18, 24, 38, 0.95)" }}
      >
        <div className="w-10 h-10 rounded-lg bg-brand-cyan/10 flex items-center justify-center shrink-0">
          <Download className="w-5 h-5 text-brand-cyan" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-body text-foreground font-medium">Instalar Nexo no celular?</p>
          <p className="text-caption text-foreground-muted truncate">
            Vira app na tela inicial, abre em tela cheia.
          </p>
        </div>
        {platform === "android" && deferredPrompt ? (
          <button
            type="button"
            onClick={installAndroid}
            data-testid="pwa-install-android"
            className="px-3 py-2 rounded-lg bg-brand-cyan text-background text-sm font-medium hover:bg-brand-cyan/90 transition-colors"
          >
            Instalar
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setIosModal(true)}
            data-testid="pwa-install-ios-help"
            className="px-3 py-2 rounded-lg bg-brand-cyan text-background text-sm font-medium hover:bg-brand-cyan/90 transition-colors"
          >
            Como
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dispensar"
          data-testid="pwa-install-dismiss"
          className="p-1 text-foreground-subtle hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Modal iOS — instruções manuais (Safari não tem prompt nativo) */}
      {iosModal && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setIosModal(false)}
          data-testid="pwa-install-ios-modal"
        >
          <div
            className="max-w-sm w-full rounded-xl bg-surface-card border border-subtle p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-h3 text-foreground">Instalar no iPhone</h2>
            <ol className="space-y-3 text-body text-foreground-muted">
              <li className="flex items-start gap-3">
                <span className="text-brand-cyan font-mono text-sm pt-0.5">1.</span>
                <span className="flex-1 inline-flex items-center gap-2">
                  Toque no botão <Share className="w-4 h-4 inline text-brand-cyan" aria-label="Compartilhar" /> Compartilhar
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-brand-cyan font-mono text-sm pt-0.5">2.</span>
                <span className="flex-1 inline-flex items-center gap-2">
                  Role e toque em <Plus className="w-4 h-4 inline text-brand-cyan" aria-label="Adicionar" /> "Adicionar à Tela de Início"
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-brand-cyan font-mono text-sm pt-0.5">3.</span>
                <span className="flex-1">Pronto — o ícone do Nexo aparece na home.</span>
              </li>
            </ol>
            <button
              type="button"
              onClick={dismiss}
              className="w-full px-4 py-3 rounded-lg bg-brand-cyan text-background font-medium hover:bg-brand-cyan/90 transition-colors"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Exports auxiliares pra testes (source-reading não precisa, mas é
// limpinho ter contratos isolados).
export const _internal = { _isIOS, _isStandalone, _wasDismissedRecently };
